<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  unit: { type: Object, default: null },
  agents: { type: Array, default: () => [] },
  roles: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'submit']);
const { t } = useI18n();

const rows = ref([]);

watch(
  () => [props.show, props.unit],
  () => {
    if (!props.show || !props.unit) return;
    rows.value = (props.unit.members || []).map(member => ({
      user_id: member.user_id,
      access_role_id: member.access_role_id,
    }));
  },
  { immediate: true }
);

const usedIds = computed(() => rows.value.map(row => row.user_id));

const availableAgents = computed(() =>
  props.agents.filter(agent => !usedIds.value.includes(agent.id))
);

const addRow = () => {
  const next = availableAgents.value[0];
  if (!next) return;
  rows.value.push({ user_id: next.id, access_role_id: null });
};

const removeRow = index => rows.value.splice(index, 1);

const agentOptionsFor = index =>
  props.agents.filter(
    agent =>
      !usedIds.value.includes(agent.id) ||
      agent.id === rows.value[index].user_id
  );

const onSubmit = () =>
  emit('submit', {
    id: props.unit.id,
    members: rows.value.filter(row => row.user_id),
  });
</script>

<template>
  <Modal :show="show" @close="emit('close')">
    <form class="flex flex-col gap-4 p-8" @submit.prevent="onSubmit">
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('ORG_ACCESS.MEMBERS.TITLE', { name: unit?.name }) }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('ORG_ACCESS.MEMBERS.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex flex-col gap-2 max-h-80 overflow-y-auto">
        <p v-if="!rows.length" class="text-sm text-n-slate-11">
          {{ t('ORG_ACCESS.MEMBERS.EMPTY') }}
        </p>
        <div
          v-for="(row, index) in rows"
          :key="`row-${index}`"
          class="flex items-end gap-2"
        >
          <label class="flex flex-col flex-1 gap-1 text-xs text-n-slate-11">
            {{ t('ORG_ACCESS.MEMBERS.AGENT') }}
            <select v-model="row.user_id">
              <option
                v-for="agent in agentOptionsFor(index)"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }} ({{ agent.email }})
              </option>
            </select>
          </label>
          <label class="flex flex-col flex-1 gap-1 text-xs text-n-slate-11">
            {{ t('ORG_ACCESS.MEMBERS.ROLE') }}
            <select v-model="row.access_role_id">
              <option :value="null">
                {{ t('ORG_ACCESS.STRUCTURE.NO_ROLE') }}
              </option>
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ role.name }}
              </option>
            </select>
          </label>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            color="ruby"
            :label="t('ORG_ACCESS.MEMBERS.REMOVE')"
            @click="removeRow(index)"
          />
        </div>
      </div>

      <Button
        type="button"
        size="sm"
        variant="ghost"
        :disabled="!availableAgents.length"
        :label="t('ORG_ACCESS.MEMBERS.ADD')"
        @click="addRow"
      />

      <div class="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          :label="t('ORG_ACCESS.UNIT_FORM.CANCEL')"
          @click="emit('close')"
        />
        <Button type="submit" :label="t('ORG_ACCESS.MEMBERS.SAVE')" />
      </div>
    </form>
  </Modal>
</template>
